import {
  errorResult,
  normalizePrescription,
  Prescription,
  PrescriptionData,
  PRESCRIPTION_VERSION,
  Result,
  Source,
  successResult,
  tu,
} from "beitary-shared";
import {
  collection,
  deleteDoc,
  doc,
  Firestore,
  onSnapshot,
  query,
  setDoc,
  Unsubscribe,
  updateDoc,
  where,
} from "firebase/firestore";

// add organization consultation

interface AddPrescription {
  ({
    db,
    organizationId,
    authorId,
    authorName,
    source,
    data,
  }: {
    db: Firestore;
    organizationId: string;
    authorId: string;
    authorName: string;
    source: Source;
    data: PrescriptionData;
  }): Promise<Result<Prescription | null>>;
}

const addPrescription: AddPrescription = async ({
  db,
  organizationId,
  authorId,
  authorName,
  source,
  data,
}) => {
  try {
    // create a new ref to get a new consultation id
    const newRef = doc(
      collection(db, "organizations", organizationId, "prescriptions")
    );

    const newObj: Prescription = normalizePrescription({
      ...data,
      id: newRef.id,
      authorId,
      authorName,
      version: PRESCRIPTION_VERSION,
      source,
      createdAt: tu.getCurrentDateTime(),
      lastUpdatedAt: tu.getCurrentDateTime(),
    });

    await setDoc(newRef, newObj);

    // t("PRESCRIPTION_CREATED")
    const successMessage = "PRESCRIPTION_CREATED";
    return successResult({
      message: successMessage,
      payload: newObj,
    });
  } catch (err: any) {
    return errorResult({ message: err.message });
  }
};

interface UpdatePrescription {
  ({
    db,
    organizationId,
    authorId,
    authorName,
    id,
    source,
    data,
  }: {
    db: Firestore;
    organizationId: string;
    authorId: string;
    authorName: string;
    id: string;
    source: Source;
    data: Partial<PrescriptionData>;
  }): Promise<Result<Partial<PrescriptionData> | null>>;
}

const updatePrescription: UpdatePrescription = async ({
  db,
  organizationId,
  authorId,
  authorName,
  id,
  source,
  data,
}) => {
  try {
    const docRef = doc(
      db,
      "organizations",
      organizationId,
      "prescriptions",
      id
    );

    const updates: Partial<Prescription> = {
      ...data,
      authorId,
      authorName,
      version: PRESCRIPTION_VERSION,
      source,
      createdAt: tu.getCurrentDateTime(),
      lastUpdatedAt: tu.getCurrentDateTime(),
    };

    await updateDoc(docRef, updates);

    // t("PRESCRIPTION_UPDATED")
    const successMessage = "PRESCRIPTION_UPDATED";

    return successResult({
      message: successMessage,
      payload: updates,
    });
  } catch (err: any) {
    console.log(err.message);
    return errorResult({ message: err.message });
  }
};

interface DeletePrescription {
  ({
    db,
    organizationId,
    id,
  }: {
    db: Firestore;
    organizationId: string;
    id: string;
  }): Promise<Result<boolean | null>>;
}

const deletePrescription: DeletePrescription = async ({
  db,
  organizationId,
  id,
}) => {
  try {
    const docRef = doc(
      db,
      "organizations",
      organizationId,
      "prescriptions",
      id
    );

    try {
      await deleteDoc(docRef);
      // t("PRESCRIPTION_DELETED")
      const successMessage = "PRESCRIPTION_DELETED";
      return successResult({
        message: successMessage,
        payload: true,
      });
    } catch (error: any) {
      console.log(error);
      return errorResult({ message: error.message });
    }
  } catch (err: any) {
    console.log(err);
    return errorResult({ message: err.message });
  }
};

// get organization prescriptions listener
interface GetConsultationPrescriptionsListenerCallback {
  (prescriptions: Prescription[]): void;
}
interface GetConsultationPrescriptionsListener {
  db: Firestore;
  organizationId: string;
  consultationId: string;
  callback: GetConsultationPrescriptionsListenerCallback;
}

const getConsultationPrescriptionsListener = ({
  db,
  organizationId,
  consultationId,
  callback,
}: GetConsultationPrescriptionsListener): Unsubscribe => {
  try {
    // console.log("getConsultationPrescriptionsListener: new listener");
    const newQuery = query(
      collection(db, "organizations", organizationId, "prescriptions"),
      where("consultationId", "==", consultationId)
    );
    return onSnapshot(newQuery, (querySnapshot) => {
      const prescriptions: Prescription[] = [];
      querySnapshot.forEach((doc) => {
        try {
          prescriptions.push(normalizePrescription(doc.data()));
        } catch (err) {
          console.log(err);
        }
      });
      callback(prescriptions);
    });
  } catch (err: any) {
    console.log(err);
    return () => {};
  }
};

// we inject dependencies to improve testability
export const prescriptions = ({
  authorId,
  authorName,
  db,
  organizationId,
  source,
}: {
  db: Firestore;
  organizationId: string;
  authorId: string;
  authorName: string;
  source: Source;
}) => {
  return {
    getConsultationPrescriptionsListener: (args: {
      callback: GetConsultationPrescriptionsListenerCallback;
      consultationId: string;
    }) =>
      getConsultationPrescriptionsListener({
        db,
        organizationId,
        ...args,
      }),
    addPrescription: (data: PrescriptionData) =>
      addPrescription({
        db,
        organizationId,
        authorId,
        authorName,
        source,
        data,
      }),
    updatePrescription: (id: string, data: Partial<PrescriptionData>) =>
      updatePrescription({
        db,
        organizationId,
        authorId,
        authorName,
        id,
        source,
        data,
      }),
    deletePrescription: (id: string) =>
      deletePrescription({
        db,
        organizationId,
        id,
      }),
  };
};
